import { Button } from '@/components/ui/button';
import {
  useDeletePreboardingCover,
  useUploadPreboardingCover,
} from '@/hooks/use-preboarding-forms';
import type { PreboardingForm } from '@hygge/shared';
import { ImagePlus, Loader2, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { CoverCropDialog } from './cover-crop-dialog';

interface CoverImageFieldProps {
  formId: string;
  coverImageUrl: string | null;
  /** Called with the server-updated form after upload / remove. */
  onUpdated: (form: PreboardingForm) => void;
}

function resolveUrl(url: string | null): string | null {
  if (!url) return null;
  return url.startsWith('http') ? url : `${window.location.origin}${url}`;
}

export function CoverImageField({ formId, coverImageUrl, onUpdated }: CoverImageFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const uploadMutation = useUploadPreboardingCover();
  const deleteMutation = useDeletePreboardingCover();

  const pickFile = () => fileInputRef.current?.click();

  const clearCropSrc = () => {
    setCropSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const onFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    setCropSrc(URL.createObjectURL(file));
  };

  const handleCropped = async (blob: Blob) => {
    const form = await uploadMutation.mutateAsync({ id: formId, file: blob });
    clearCropSrc();
    onUpdated(form);
  };

  const handleRemove = async () => {
    const form = await deleteMutation.mutateAsync(formId);
    onUpdated(form);
  };

  const resolvedUrl = resolveUrl(coverImageUrl);

  return (
    <div className="space-y-2">
      {resolvedUrl ? (
        <div className="relative aspect-video overflow-hidden rounded-lg border border-border bg-muted">
          <img src={resolvedUrl} alt="Cover" className="h-full w-full object-cover" />
          {uploadMutation.isPending && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/60">
              <Loader2 className="h-6 w-6 animate-spin text-foreground" />
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={pickFile}
          className="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/30 text-muted-foreground transition-colors hover:border-hygge/40 hover:text-foreground"
        >
          <ImagePlus className="h-7 w-7" />
          <span className="text-sm">Upload a cover image</span>
          <span className="text-xs">JPG, PNG or WebP · recommended 1600×900 (16:9)</span>
        </button>
      )}

      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={pickFile} disabled={uploadMutation.isPending}>
          <Upload className="mr-1.5 h-4 w-4" />
          {resolvedUrl ? 'Replace' : 'Upload'}
        </Button>
        {resolvedUrl && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            disabled={deleteMutation.isPending}
            className="text-muted-foreground hover:text-destructive"
          >
            {deleteMutation.isPending ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="mr-1.5 h-4 w-4" />
            )}
            Remove
          </Button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFileSelected}
      />

      <CoverCropDialog
        open={!!cropSrc}
        imageSrc={cropSrc}
        onOpenChange={(open) => {
          if (!open) clearCropSrc();
        }}
        onCropped={handleCropped}
        isUploading={uploadMutation.isPending}
      />
    </div>
  );
}
